import React, { useMemo, useRef, useState } from 'react'
import Table from '../../../../components/Table/Table'
import AddAccountManagerModal from './AddAccountManagerModal'
import Modal from '../../../../components/Modal'
import { impersonateApi } from './../../../../apis/apis'
import { formatPhoneNumber } from '../../../../util/string'
import { AccountManager } from '../../../../apis/types'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { format, formatDistanceToNow } from 'date-fns'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import { disableAccountManagerApi, generatePasswordResetLinkAccountManagerApi, resetPasswordAccountManagerApi } from './../../../../apis/apis'
import classnames from 'classnames'
import Button from '../../../../components/Form/Button'
import ActionMenu from '../../../../components/ActionMenu'
import Badge from '../../../../components/Badge'
import { useTabHeaderActions } from './TabPanel'

interface TabAccountManagersProps {
  title: string
  badge?: number | string
  data: AccountManager[]
  tenantAccountCuid: string
}

const TabAccountManagers: React.FC<TabAccountManagersProps> = (props) => {
  const { tenantAccountCuid, data, title: _title, badge: _badge, ...rest } = props
  const { permissions } = useAppSelector((state) => state.app)
  const dispatch = useAppDispatch()

  const [confirmAccountManagerDisableModal, setConfirmAccountManagerDisableModal] = useState<React.ReactElement | boolean>(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  const { inactiveManagers, visibleManagers } = useMemo(() => {
    const active = data.filter((am) => am.isActive !== false)
    const inactive = data.filter((am) => am.isActive === false)
    return {
      inactiveManagers: inactive,
      visibleManagers: showInactive ? data : active,
    }
  }, [data, showInactive])

  const hasAccountManagerCreatePermission = (): boolean => {
    if (permissions == null) return false
    return permissions.includes('account:manager:create')
  }

  const openAddModalRef = useRef(() => setIsAddModalOpen(true))
  openAddModalRef.current = () => setIsAddModalOpen(true)

  const addHeaderAction = useMemo(
    () => (
      <Button onClick={() => openAddModalRef.current()} size={2}>
        Add account manager
      </Button>
    ),
    []
  )
  useTabHeaderActions(addHeaderAction, hasAccountManagerCreatePermission())

  const hasImpersonatePermission = (): boolean => {
    if (permissions == null) return false
    return permissions.includes('impersonate:tenant')
  }

  const hasCopyAccountInvitationPermission = (): boolean => {
    if (permissions == null) return false
    return permissions.includes('account:manager:copyinvite')
  }

  const hasDisableAccountManagerPermission = (): boolean => {
    if (permissions == null) return false

    return permissions.includes('account:manager:disable')
  }

  const hasResetPasswordPermission = (): boolean => {
    if (permissions == null) return false

    return permissions.includes('account:manager:password:reset')
  }

  const handleImpersonate = async (accountManager: AccountManager): Promise<void> => {
    const { impersonationToken, tokenExpirationSeconds } = await impersonateApi(accountManager.id)

    const impersonationWindow = window.open('/impersonate')
    impersonationWindow != null && (impersonationWindow.window.impersonation_ephemeralToken = impersonationToken)
    impersonationWindow != null && (impersonationWindow.window.impersonation_tokenExpirationSeconds = tokenExpirationSeconds)
    impersonationWindow != null && (impersonationWindow.window.impersonation_email = accountManager.email)
    impersonationWindow != null && (impersonationWindow.window.impersonation_tenantAccountCuid = tenantAccountCuid)
  }

  const handleDisableAccountManager = async (accountManager: AccountManager): Promise<void> => {
    try {
      await new Promise((resolve, reject) => {
        setConfirmAccountManagerDisableModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmAccountManagerDisableModal(false)
      try {
        const payload = {
          tenantAccountId: tenantAccountCuid,
          emailAddress: accountManager.email,
        }

        await disableAccountManagerApi(payload)

        await dispatch(
          pushToast({
            message: 'Account manager disabled',
            type: 'success',
          })
        )

        window.location.reload()
      } catch (e: any) {
        await dispatch(
          pushToast({
            message: 'Failed to disable account manager',
            type: 'error',
            description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      }
    } catch (e) {
      setConfirmAccountManagerDisableModal(false)
    }
  }

  const handleCopyInvitationToClipboard = async (accountManager: AccountManager): Promise<void> => {
    const { accountSignupLink } = accountManager
    if (accountSignupLink == null) return
    try {
      await navigator.clipboard.writeText(accountSignupLink)
      await dispatch(
        pushToast({
          message: 'Account invitation link copied to clipboard',
          type: 'success',
        })
      )
    } catch (e: any) {
      await dispatch(
        pushToast({
          message: 'Failed to copy account invitation link to clipboard',
          type: 'error',
          description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
        })
      )
    }
  }

  const handleCopyPasswordResetLink = async (accountManager: AccountManager): Promise<void> => {
    try {
      const payload = {
        tenantAccountId: tenantAccountCuid,
        emailAddress: accountManager.email,
      }

      const { passwordResetLink, expiresInDays } = await generatePasswordResetLinkAccountManagerApi(payload)
      await navigator.clipboard.writeText(passwordResetLink)

      await dispatch(
        pushToast({
          message: 'Password reset link copied to clipboard',
          type: 'success',
          description: `One-time use, valid for ${expiresInDays} day${expiresInDays === 1 ? '' : 's'}. Sending another reset will invalidate this link.`,
        })
      )
    } catch (e: any) {
      await dispatch(
        pushToast({
          message: 'Failed to copy password reset link',
          type: 'error',
          description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
        })
      )
    }
  }

  const handleSendPasswordResetEmail = async (accountManager: AccountManager): Promise<void> => {
    try {
      const payload = {
        tenantAccountId: tenantAccountCuid,
        emailAddress: accountManager.email,
      }

      await resetPasswordAccountManagerApi(payload)

      await dispatch(
        pushToast({
          message: 'Password reset email sent',
          type: 'success',
          description: `A reset link was emailed to ${accountManager.email}. Generating another reset will invalidate the previous link.`,
        })
      )
    } catch (e: any) {
      await dispatch(
        pushToast({
          message: 'Failed to send password reset email',
          type: 'error',
          description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
        })
      )
    }
  }

  const handlePasswordResetAction = async (accountManager: AccountManager, action?: string): Promise<void> => {
    if (action === 'copy-link') {
      await handleCopyPasswordResetLink(accountManager)
      return
    }

    if (action === 'send-email') {
      await handleSendPasswordResetEmail(accountManager)
    }
  }

  return (
    <>
      {data.length > 0 ? (
        <div className="space-y-2">
          {visibleManagers.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No active account managers</p>
          ) : (
            <Table<AccountManager>
              compact
              stickyHeader
              scrollShell={{ heightClassName: 'max-h-64 sm:max-h-80 md:max-h-96 lg:max-h-[45vh]' }}
              colConfig={[
                {
                  label: 'Name',
                  render: (record) => (
                    <div
                      className={classnames(
                        'flex min-w-0 flex-wrap items-center gap-2',
                        record.isActive === false && 'text-slate-500 dark:text-slate-400'
                      )}
                    >
                      <span className="min-w-0 truncate">{`${record.profile?.firstName ?? ''} ${record.profile?.lastName ?? ''}`}</span>
                      {record.isActive === false && <Badge status="INACTIVE" />}
                    </div>
                  ),
                },
                {
                  label: 'Email Address',
                  render: (record) => (
                    <div className={classnames(record.isActive === false ? 'text-slate-500 dark:text-slate-400' : '')}>
                      {record.email}
                    </div>
                  ),
                },
                {
                  label: 'Phone number',
                  render: (record) => (
                    <div className={classnames(record.isActive === false ? 'text-slate-500 dark:text-slate-400' : '')}>
                      {formatPhoneNumber(record.profile?.phoneNumber ?? '') ?? ''}
                    </div>
                  ),
                },
                {
                  label: 'Last Login',
                  render: (record) => {
                    if (record.lastLoginDate == null) {
                      return (
                        <div className={classnames(record.isActive === false ? 'text-slate-500 dark:text-slate-400' : '')}>
                          —
                        </div>
                      )
                    }
                    const d = new Date(record.lastLoginDate)
                    return (
                      <div className={classnames(record.isActive === false ? 'text-slate-500 dark:text-slate-400' : '')}>
                        <div>{format(d, "MMM d, yyyy 'at' h:mm aaa")}</div>
                        <div className="text-xs text-slate-400 dark:text-slate-500">
                          {formatDistanceToNow(d, { addSuffix: true })}
                        </div>
                      </div>
                    )
                  },
                },
                ...(hasCopyAccountInvitationPermission() ||
                hasImpersonatePermission() ||
                hasDisableAccountManagerPermission() ||
                hasResetPasswordPermission()
                  ? [
                      {
                        label: 'Actions',
                        skipRowLink: true,
                        overflowVisible: true,
                        render: (record: AccountManager) => (
                          <div className="flex flex-row items-center">
                            {record.isActive && hasImpersonatePermission() && (
                              <Button
                                type="button"
                                onClick={() => {
                                  void handleImpersonate(record)
                                }}
                              >
                                Impersonate
                              </Button>
                            )}
                            {record.isActive &&
                              record.accountSignupLink != null &&
                              record.accountSignupLink &&
                              hasCopyAccountInvitationPermission() && (
                                <Button
                                  type="button"
                                  className="ml-2"
                                  onClick={() => {
                                    void handleCopyInvitationToClipboard(record)
                                  }}
                                >
                                  Copy Account Invitation
                                </Button>
                              )}
                            {record.isActive && hasResetPasswordPermission() && (
                              <div className="ml-2">
                                <ActionMenu
                                  label="Password Reset"
                                  variant="button"
                                  handleChange={async (action) => {
                                    await handlePasswordResetAction(record, action)
                                  }}
                                  options={[
                                    { label: 'Copy Password Reset Link', value: 'copy-link' },
                                    { label: 'Send Password Reset Email', value: 'send-email' },
                                  ]}
                                />
                              </div>
                            )}
                            {record.isActive && hasDisableAccountManagerPermission() && (
                              <Button
                                type="button"
                                className="ml-2"
                                onClick={() => {
                                  void handleDisableAccountManager(record)
                                }}
                                color="red"
                              >
                                Disable
                              </Button>
                            )}
                          </div>
                        ),
                      },
                    ]
                  : []),
              ]}
              hidePaginator
              data={{
                results: visibleManagers,
              }}
              {...rest}
            />
          )}

          {inactiveManagers.length > 0 && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setShowInactive((v) => !v)}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                {showInactive
                  ? `Hide ${inactiveManagers.length} inactive account${inactiveManagers.length > 1 ? 's' : ''}`
                  : `Show ${inactiveManagers.length} inactive account${inactiveManagers.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </div>
      ) : (
        <span>No account managers found</span>
      )}
      <AddAccountManagerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        tenantAccountCuid={tenantAccountCuid}
      />
      {confirmAccountManagerDisableModal}
    </>
  )
}

export default TabAccountManagers
